import { useRef } from 'react';
import { createPortal } from 'react-dom';
import { View, Pressable, StyleSheet } from 'react-native';
import Webcam from 'react-webcam';
import Ionicons from '@expo/vector-icons/Ionicons';

// browser-only camera capture, backed by react-webcam since expo-image-picker has no camera support on web
export default function WebCameraCapture({ visible, onCapture, onClose }) {
  const webcamRef = useRef(null);

  const capture = () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      onCapture(imageSrc);
    }
  };

  if (!visible) {
    return null;
  }

  return (
    // rendered via a portal into document.body: react-navigation's screen wrapper applies a
    // CSS transform, which would otherwise turn our "fixed" overlay into being scoped to that
    // ancestor instead of the real viewport, letting the header stack on top of it
    createPortal(
      <Pressable style={styles.backdrop} onPress={onClose}>
        <Pressable style={styles.container} onPress={(e) => e.stopPropagation()}>
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/png"
            videoConstraints={{ facingMode: 'environment' }}
            style={styles.webcam}
          />
          <View style={styles.buttonRow}>
            <Pressable onPress={onClose} style={styles.cancelButton}>
              <Ionicons name="close" size={28} color="#674FE0" />
            </Pressable>
            <Pressable onPress={capture} style={styles.captureButton}>
              <Ionicons name="camera" size={28} color="#fff" />
            </Pressable>
          </View>
        </Pressable>
      </Pressable>,
      document.body
    )
  );
}

const styles = StyleSheet.create({
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    // dvh reflects the real visible viewport (vh can overshoot when mobile browser chrome is showing)
    height: '100dvh',
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  container: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 10,
    maxWidth: '95vw',
    maxHeight: '95dvh',
    flexDirection: 'column',
    alignItems: 'center',
    overflow: 'hidden',
  },
  webcam: {
    // flexShrink + minHeight:0 let the video shrink to whatever space is left above the
    // buttons instead of a fixed vh guess, so the button row can never be pushed off screen
    width: 'auto',
    height: 'auto',
    flexShrink: 1,
    minHeight: 0,
    maxWidth: '90vw',
    objectFit: 'contain',
    borderRadius: 8,
  },
  buttonRow: {
    flexShrink: 0,
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    marginTop: 10,
  },
  cancelButton: {
    padding: 10,
  },
  captureButton: {
    backgroundColor: '#674FE0',
    padding: 12,
    borderRadius: 30,
  },
});
